/**
 * @file RepsManager.js
 * @version 3.1.5
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-01-19
 * @modified 2026-10-01
 * @description Manage sales reps locally (no Xero sync). Supports name/email editing and archiving.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import { useSlideOutButton } from './SlideOutButton';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import './RepsManager.css';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { useReveal } from '../utils/revealInSearch';

// What Clear Filters resets the search box and archived toggle to.
const CLEARED_FILTERS = { search: '', showArchived: false };

function RepsManager() {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  // Arriving from a modal's "Find in …" (utils/revealInSearch.js): searched for.
  const reveal = useReveal('reps');
  const [searchTerm, setSearchTerm] = useState(reveal?.term || '');
  const [showArchived, setShowArchived] = useState(false);

  // Name editing state
  const [editingNameId, setEditingNameId] = useState(null);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Email editing state
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [emailValue, setEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Phone editing state
  const [editingPhoneId, setEditingPhoneId] = useState(null);
  const [phoneValue, setPhoneValue] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);

  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: null });

  const fetchReps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/sales-reps?includeArchived=${showArchived}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) setReps(data.reps || []);
    } catch (err) {
      console.error('Failed to fetch reps:', err);
      setAlertDialog({ isOpen: true, message: 'Failed to load sales reps', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { fetchReps(); }, [fetchReps]);

  // ── Name editing ──────────────────────────────────────────────
  const startEditName = (rep) => {
    if (editingEmailId) return;
    setEditingNameId(rep.id);
    setNameValue(rep.name);
  };

  const cancelEditName = () => { setEditingNameId(null); setNameValue(''); };

  const saveNameEdit = async (repId) => {
    const trimmed = nameValue.trim();
    if (!trimmed) return;
    if (trimmed === reps.find(r => r.id === repId)?.name) {
      cancelEditName();
      return;
    }
    setSavingName(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/sales-reps/${repId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed })
      });
      const data = await res.json();
      if (data.success) {
        setReps(prev => prev.map(r => r.id === repId ? { ...r, name: data.rep.name } : r));
        setEditingNameId(null);
        setNameValue('');
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to update name', type: 'error' });
      }
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update name', type: 'error' });
    } finally {
      setSavingName(false);
    }
  };

  // ── Email editing ─────────────────────────────────────────────
  const startEditEmail = (rep) => {
    if (editingNameId) return;
    setEditingEmailId(rep.id);
    setEmailValue(rep.email || '');
  };

  const cancelEditEmail = () => { setEditingEmailId(null); setEmailValue(''); };

  const saveEmailEdit = async (repId) => {
    setSavingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/sales-reps/${repId}/email`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue.trim() || null })
      });
      const data = await res.json();
      if (data.success) {
        setReps(prev => prev.map(r => r.id === repId ? { ...r, email: data.rep.email } : r));
        setEditingEmailId(null);
        setEmailValue('');
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to update email', type: 'error' });
      }
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update email', type: 'error' });
    } finally {
      setSavingEmail(false);
    }
  };

  // ── Phone editing ────────────────────────────────────────────
  const startEditPhone = (rep) => {
    if (editingNameId || editingEmailId) return;
    setEditingPhoneId(rep.id);
    setPhoneValue(rep.phone || '');
  };

  const cancelEditPhone = () => { setEditingPhoneId(null); setPhoneValue(''); };

  const savePhoneEdit = async (repId) => {
    setSavingPhone(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/sales-reps/${repId}/phone`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneValue.trim() || null })
      });
      const data = await res.json();
      if (data.success) {
        setReps(prev => prev.map(r => r.id === repId ? { ...r, phone: data.rep.phone } : r));
        setEditingPhoneId(null);
        setPhoneValue('');
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to update phone', type: 'error' });
      }
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update phone', type: 'error' });
    } finally {
      setSavingPhone(false);
    }
  };

  // ── Archive ───────────────────────────────────────────────────
  const handleArchive = (rep) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Archive Sales Rep',
      message: `Archive "${rep.name}"? They will no longer appear in order dropdowns.`,
      type: 'warning',
      confirmText: 'Archive',
      onConfirm: () => doArchiveRep(rep)
    });
  };

  const doArchiveRep = async (rep) => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    try {
      const res = await fetch(`${API_URL}/api/orders/sales-reps/${rep.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        if (!showArchived) {
          setReps(prev => prev.filter(r => r.id !== rep.id));
        } else {
          setReps(prev => prev.map(r => r.id === rep.id ? { ...r, status: 'ARCHIVED' } : r));
        }
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to archive rep', type: 'error' });
      }
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to archive rep', type: 'error' });
    }
  };

  // ── Add rep ───────────────────────────────────────────────────
  const handleOpenAdd = () => {
    setNewName('');
    setNewEmail('');
    setModalError('');
    setShowAddModal(true);
  };

  const handleSaveNew = async () => {
    const trimmed = newName.trim();
    if (!trimmed) { setModalError('Rep name is required'); return; }
    if (trimmed.length > 100) { setModalError('Name must be 100 characters or less'); return; }

    setSaving(true);
    setModalError('');
    try {
      const res = await fetch(`${API_URL}/api/orders/sales-reps`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed })
      });
      const data = await res.json();
      if (data.success) {
        const rep = data.rep;
        // If email was provided, save it too
        if (newEmail.trim()) {
          await fetch(`${API_URL}/api/orders/sales-reps/${rep.id}/email`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: newEmail.trim() })
          });
          rep.email = newEmail.trim();
        }
        setReps(prev => [...prev, rep].sort((a, b) => a.name.localeCompare(b.name)));
        setShowAddModal(false);
      } else {
        setModalError(data.error || 'Failed to create rep');
      }
    } catch (err) {
      setModalError('Failed to create rep. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const filteredReps = reps.filter(rep =>
    matchesAllTerms([rep.name, rep.email], searchTerm)
  );

  const ctxMenu = useTableContextMenu({
    rows: filteredReps,
    getRowId: (rep) => rep.id
  });

  const buildRowActions = (rep) => [
    { key: 'email', icon: 'mail', label: 'Edit', title: 'Edit email', onClick: () => startEditEmail(rep) },
    ...(rep.status === 'ACTIVE' ? [
      { key: 'archive', icon: 'archive', label: 'Archive', title: 'Archive rep', className: 'btn-danger', onClick: () => handleArchive(rep) },
    ] : []),
  ];

  // Slideout button sizing
  useSlideOutButton([showAddModal]);

  // Any header filter applied lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(
    { search: searchTerm, showArchived }, CLEARED_FILTERS, { labels: { showArchived: 'Archived' } }
  );
  const filtersApplied = headerFilters.count > 0;

  return (
    <div className="search-layout reps-manager" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Manage Sales Reps</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>Manage sales reps and their email addresses for order assignment.</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons">
            <button
              className={clearFiltersClass(filtersApplied)}
              onClick={() => { setSearchTerm(CLEARED_FILTERS.search); setShowArchived(CLEARED_FILTERS.showArchived); }}
              title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">filter_alt_off</span>
              </div>
              <span className="btn-label">Clear Filters</span>
            </button>
            <button className="btn-slideout" onClick={handleOpenAdd}>
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">add</span>
              </div>
              <span className="btn-label">Add Rep</span>
            </button>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr auto' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['name', 'email']} />
            </label>
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-archived-reps"
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                <label htmlFor="show-archived-reps"></label>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="search-content">
        <div className="table-container">
          {loading ? (
            <LoadingSpinner />
          ) : filteredReps.length === 0 ? (
            <div className="no-results">
              <span className="material-symbols-rounded">group</span>
              <p>{searchTerm ? 'No matching reps found' : 'No sales reps available. Click "Add Rep" to create one.'}</p>
            </div>
          ) : (
            <table className="search-results-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th className='no-sort'>Name</th>
                  <th className='no-sort'>Email Address</th>
                  <th className='no-sort'>Phone</th>
                  <th className="status-col">Status</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {filteredReps.map(rep => (
                  <tr
                    key={rep.id}
                    className={rep.status !== 'ACTIVE' ? 'rm-archived-row' : ''}
                    onContextMenu={ctxMenu.onRowContextMenu(rep)}
                  >
                    {/* Name cell */}
                    <td className="rm-name-cell">
                      {editingNameId === rep.id ? (
                        <div className="rm-inline-edit">
                          <input
                            type="text"
                            className="rm-edit-input"
                            value={nameValue}
                            onChange={(e) => setNameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveNameEdit(rep.id);
                              if (e.key === 'Escape') cancelEditName();
                            }}
                            maxLength={100}
                            autoFocus
                            disabled={savingName}
                          />
                          <button className="btn-icon rm-btn-save" onClick={() => saveNameEdit(rep.id)} disabled={savingName} title="Save">
                            <span className="material-symbols-rounded">check</span>
                          </button>
                          <button className="btn-icon" onClick={cancelEditName} disabled={savingName} title="Cancel">
                            <span className="material-symbols-rounded">close</span>
                          </button>
                        </div>
                      ) : (
                        <span className="rm-name-display" onClick={() => startEditName(rep)} title="Click to edit name">
                          <strong>{rep.name}</strong>
                        </span>
                      )}
                    </td>

                    {/* Email cell */}
                    <td className="rm-email-cell">
                      {editingEmailId === rep.id ? (
                        <div className="rm-inline-edit">
                          <input
                            type="email"
                            className="rm-edit-input"
                            value={emailValue}
                            onChange={(e) => setEmailValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEmailEdit(rep.id);
                              if (e.key === 'Escape') cancelEditEmail();
                            }}
                            placeholder="Enter email address"
                            autoFocus
                            disabled={savingEmail}
                          />
                          <button className="btn-icon rm-btn-save" onClick={() => saveEmailEdit(rep.id)} disabled={savingEmail} title="Save">
                            <span className="material-symbols-rounded">check</span>
                          </button>
                          <button className="btn-icon" onClick={cancelEditEmail} disabled={savingEmail} title="Cancel">
                            <span className="material-symbols-rounded">close</span>
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`rm-email-display ${!rep.email ? 'rm-empty' : ''}`}
                          onClick={() => startEditEmail(rep)}
                          title="Click to edit email"
                        >
                          {rep.email || 'Click to add email'}
                        </span>
                      )}
                    </td>

                    {/* Phone cell */}
                    <td className="rm-phone-cell">
                      {editingPhoneId === rep.id ? (
                        <div className="rm-inline-edit">
                          <input
                            type="tel"
                            className="rm-edit-input"
                            value={phoneValue}
                            onChange={(e) => setPhoneValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') savePhoneEdit(rep.id);
                              if (e.key === 'Escape') cancelEditPhone();
                            }}
                            placeholder="Enter phone number"
                            autoFocus
                            disabled={savingPhone}
                          />
                          <button className="btn-icon rm-btn-save" onClick={() => savePhoneEdit(rep.id)} disabled={savingPhone} title="Save">
                            <span className="material-symbols-rounded">check</span>
                          </button>
                          <button className="btn-icon" onClick={cancelEditPhone} disabled={savingPhone} title="Cancel">
                            <span className="material-symbols-rounded">close</span>
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`rm-email-display ${!rep.phone ? 'rm-empty' : ''}`}
                          onClick={() => startEditPhone(rep)}
                          title="Click to edit phone"
                        >
                          {rep.phone || 'Click to add phone'}
                        </span>
                      )}
                    </td>

                    <td className="status-col">
                      <span className={`status-badge ${rep.status === 'ACTIVE' ? 'status-active' : 'status-archived'}`}>
                        {rep.status}
                      </span>
                    </td>

                    <td className="actions-col">
                      {editingNameId !== rep.id && editingEmailId !== rep.id && editingPhoneId !== rep.id && (
                        <RowActionsMenu actions={buildRowActions(rep)} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Rep Modal */}
      {showAddModal && createPortal(
        <div className="modal-overlay" onClick={() => !saving && setShowAddModal(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Sales Rep</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)} disabled={saving}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: 'var(--spacing-sm) var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
              {modalError && (
                <div style={{
                  padding: 'var(--spacing-sm)',
                  background: 'rgba(var(--color-error-rgb), 0.1)',
                  border: '1px solid var(--color-error)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--color-error)',
                  marginBottom: 'var(--spacing-md)',
                  fontSize: 'var(--font-sm)'
                }}>{modalError}</div>
              )}
              <div className="form-group">
                <label>Name <span style={{ color: 'var(--color-error)' }}>*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter rep name"
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleSaveNew(); }}
                />
              </div>
              <div className="form-group">
                <label>Email Address <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>(optional)</span></label>
                <input
                  type="email"
                  className="form-control"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter email address"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleSaveNew(); }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <div>
                <button className='btn-slideout' onClick={() => setShowAddModal(false)} disabled={saving}>
                  <div className='icon-wrapper'>
                    <span className='material-symbols-rounded'>close</span>
                  </div>
                  <span className='btn-label'>Cancel</span>
                </button>
              </div>
              <div>
                <button className='btn-slideout' onClick={handleSaveNew} disabled={saving || !newName.trim()}>
                  <div className='icon-wrapper'>
                    <span className='material-symbols-rounded'>save</span>
                  </div>
                  <span className='btn-label'>{saving ? 'Saving...' : 'Save'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>, document.body
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        onConfirm={() => { if (confirmDialog.onConfirm) confirmDialog.onConfirm(); }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ isOpen: false, message: '', type: 'info' })}
      />

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add rep', onClick: handleOpenAdd },
          { key: 'refresh', icon: 'refresh', label: 'Refresh', onClick: fetchReps },
          {
            key: 'show-archived',
            icon: 'inventory_2',
            label: 'Show archived',
            active: showArchived,
            onClick: () => setShowArchived(v => !v)
          },
          {
            key: 'clear-filters',
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: () => { setSearchTerm(''); setShowArchived(false); }
          }
        ]}
      />
    </div>
  );
}

export default RepsManager;
